/**
 * useActivityHeartbeat.js
 *
 * Lightweight presence beacon for the Member Activity tracking. While the user
 * is logged in, the tab is visible, and they have interacted recently, we ping
 * the `recordActivityHeartbeat` Cloud Function every HEARTBEAT_INTERVAL_MS for
 * the currently active world. The backend turns these pings into per-day
 * active-time + session rollups (member_activity/{uid}/days/{day}).
 *
 * Design goals:
 * - Cheap: one tiny callable every ~30s, only while genuinely active.
 * - Idle-aware: no beats after IDLE_TIMEOUT_MS without interaction.
 * - Tab-aware: pauses on hidden tab, fires immediately on focus regain.
 */

import { useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../firebaseConfig';
import { useTeam } from '../contexts/TeamContext';

const HEARTBEAT_INTERVAL_MS = 30_000;
const IDLE_TIMEOUT_MS = 5 * 60_000;

export default function useActivityHeartbeat() {
  const { currentUserId, activeWorldId, isSwitching } = useTeam();

  const lastInteractionRef = useRef(Date.now());
  const lastBeatRef = useRef(0);
  const inFlightRef = useRef(false);
  const worldRef = useRef(activeWorldId);
  const switchingRef = useRef(isSwitching);

  useEffect(() => { worldRef.current = activeWorldId; }, [activeWorldId]);
  useEffect(() => { switchingRef.current = isSwitching; }, [isSwitching]);

  // Track user interaction so we only beat while actually active.
  useEffect(() => {
    const markInteraction = () => {
      lastInteractionRef.current = Date.now();
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, markInteraction, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, markInteraction));
    };
  }, []);

  useEffect(() => {
    if (!currentUserId) return undefined;

    const callable = httpsCallable(functions, 'recordActivityHeartbeat');

    const sendBeat = async (force = false) => {
      if (inFlightRef.current) return;
      if (!auth.currentUser) return;
      if (document.visibilityState !== 'visible') return;
      if (switchingRef.current) return;

      const now = Date.now();
      const idleFor = now - lastInteractionRef.current;
      if (!force && idleFor > IDLE_TIMEOUT_MS) return;
      if (!force && now - lastBeatRef.current < HEARTBEAT_INTERVAL_MS - 1_000) return;

      const worldUid = worldRef.current || currentUserId;

      inFlightRef.current = true;
      lastBeatRef.current = now;
      try {
        await callable({ worldUid });
      } catch (_) {
        // Presence is best-effort; swallow errors (offline, cold start, etc).
      } finally {
        inFlightRef.current = false;
      }
    };

    // Fire one beat shortly after mount so short sessions still register.
    const initialTimer = setTimeout(() => sendBeat(true), 4_000);

    const interval = setInterval(() => sendBeat(false), HEARTBEAT_INTERVAL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        lastInteractionRef.current = Date.now();
        sendBeat(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [currentUserId]);
}
